import { css } from '@emotion/react';
import { Button, Typography } from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { isAxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { colors } from '../../../../theme/colors';
import { downloadBulkTearsheetsAsZip } from '../../../../services/queries/MaggieTearsheetQueries';
import { portfolioCompaniesAtom } from '../../../../services/state/PortCosStateJ';
import { dateToPlainDateString } from '../../../../util/date-utilts';
import { CompanyPreferenceSelector } from '../CompanyPreferenceSelector';
import { LayoutCanvas } from './components/LayoutCanvas';
import { PanelPalette } from './components/PanelPalette';
import { useLayoutActions } from './hooks/useLayoutActions';
import { getCanvasDropZone, getDropZone } from './hooks/useDropZoneDetection';
import { TEARSHEET_PANELS } from './constants';
import { selectedCompanyIdAtom } from './state';
import { DropTarget, TearsheetPanelType } from './types';
import { findCell } from './utils/layoutGrid';

const containerStyle = css`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0 1rem 0;
  border-bottom: 1px solid ${colors.neutral[20]};
`;

const headerLeftStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const titleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const headerActionsStyle = css`
  display: flex;
  gap: 0.5rem;
`;

const mainContentStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const dragOverlayStyle = css`
  padding: 0.75rem 1rem;
  background: white;
  border: 2px solid ${colors.primary[40]};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 500;
`;

export function TearsheetV1LayoutBuilder() {
  const { layout, usedPanels, addPanel, movePanel, removePanel, updateSizes, resetLayout } =
    useLayoutActions();
  const { pushErrorToast } = useToastMessageState();
  const [selectedCompanyId, setSelectedCompanyId] = useAtom(selectedCompanyIdAtom);
  const portfolioCompanies = useAtomValue(portfolioCompaniesAtom);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDropTarget, setActiveDropTarget] = useState<DropTarget | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleTestGenerate = useCallback(async () => {
    if (selectedCompanyId === null) {
      return;
    }
    setIsGenerating(true);
    try {
      const blob = await downloadBulkTearsheetsAsZip({
        companyIds: [selectedCompanyId],
        asOfDate: dateToPlainDateString(new Date()),
        useRendererV2: true,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const companyName = portfolioCompanies.find((c) => c.id === selectedCompanyId)?.name ?? 'tearsheet';
      link.download = `${companyName} tearsheets.zip`;
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      let errorMessage = 'Failed to generate tearsheet. Please try again.';
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403) {
          errorMessage = 'You do not have permission to generate tearsheets for this company.';
        } else if (status === 500) {
          errorMessage = 'Server error occurred while generating tearsheets. Please try again later.';
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }
      }
      pushErrorToast({ message: errorMessage });
    } finally {
      setIsGenerating(false);
    }
  }, [selectedCompanyId, portfolioCompanies, pushErrorToast]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 8,
    },
  });

  const sensors = useSensors(pointerSensor, mouseSensor, touchSensor);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  }, []);

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const { active } = event;

      // Use the translated rect from dnd-kit which accounts for scroll
      const translatedRect = active.rect.current.translated;
      if (!translatedRect) {
        return;
      }

      const pointerPosition = {
        x: translatedRect.left + translatedRect.width / 2,
        y: translatedRect.top + translatedRect.height / 2,
      };

      // Find which panel element is under the pointer
      const elementsUnderPointer = document.elementsFromPoint(pointerPosition.x, pointerPosition.y);

      // Check for panel slot first
      for (const element of elementsUnderPointer) {
        const panelId = (element as HTMLElement).dataset?.panelId;
        if (panelId) {
          const rect = element.getBoundingClientRect();
          const zone = getDropZone(rect, pointerPosition);

          // Verify the cell exists in layout
          const cell = findCell(layout.root, panelId);
          if (cell) {
            // Don't show drop target on the panel being dragged
            const activeData = active.data.current as { cellId?: string } | undefined;
            if (activeData?.cellId === panelId) {
              continue;
            }

            setActiveDropTarget({
              type: 'panel',
              cellId: panelId,
              zone,
            });
            return;
          }
        }
      }

      // Check for canvas-level drop
      const canvasElement = document.querySelector('[data-droppable-id="canvas-root"]');
      if (canvasElement) {
        const canvasRect = canvasElement.getBoundingClientRect();
        if (
          pointerPosition.x >= canvasRect.left &&
          pointerPosition.x <= canvasRect.right &&
          pointerPosition.y >= canvasRect.top &&
          pointerPosition.y <= canvasRect.bottom
        ) {
          const zone = getCanvasDropZone(canvasRect, pointerPosition, layout.root !== null);
          if (zone) {
            setActiveDropTarget({ type: 'canvas', zone });
            return;
          }
        }
      }

      setActiveDropTarget(null);
    },
    [layout.root]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active } = event;

      const activeData = active.data.current as
        | {
            type?: string;
            panelType?: TearsheetPanelType;
            cellId?: string;
          }
        | undefined;

      if (!activeDropTarget) {
        setActiveDragId(null);
        setActiveDropTarget(null);
        return;
      }

      // Dragging from palette (new panel)
      if (activeData?.type !== 'layout-panel' && activeData?.panelType) {
        addPanel(activeData.panelType, activeDropTarget);
      }
      // Dragging from layout (move existing panel)
      else if (
        activeData?.type === 'layout-panel' &&
        activeData.cellId &&
        activeDropTarget.type === 'panel' &&
        activeDropTarget.cellId
      ) {
        // Don't move if dropping on itself
        if (activeData.cellId !== activeDropTarget.cellId) {
          movePanel(activeData.cellId, activeDropTarget.cellId, activeDropTarget.zone);
        }
      }

      setActiveDragId(null);
      setActiveDropTarget(null);
    },
    [activeDropTarget, addPanel, movePanel]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragId(null);
    setActiveDropTarget(null);
  }, []);

  // Get the panel label for the drag overlay
  const getDragOverlayLabel = () => {
    if (!activeDragId) {
      return null;
    }

    // Handle palette drags (format: palette-{panelType})
    if (activeDragId.startsWith('palette-')) {
      const panelType = activeDragId.replace('palette-', '') as TearsheetPanelType;
      const panel = TEARSHEET_PANELS[panelType];
      return panel?.label ?? 'Panel';
    }

    // Handle layout panel drags (format: layout-panel-{cellId})
    if (activeDragId.startsWith('layout-panel-')) {
      const cellId = activeDragId.replace('layout-panel-', '');
      const cell = findCell(layout.root, cellId);
      if (cell && cell.type === 'panel') {
        return TEARSHEET_PANELS[cell.panelType]?.label ?? 'Panel';
      }
    }

    return 'Panel';
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div css={containerStyle}>
        <div css={headerStyle}>
          <div css={headerLeftStyle}>
            <div css={titleRowStyle}>
              <Typography variant='h6'>Tearsheet template for</Typography>
              <CompanyPreferenceSelector
                selectedCompanyId={selectedCompanyId}
                onCompanyChange={setSelectedCompanyId}
              />
            </div>
            <Typography variant='body2' color='text.secondary'>
              Drag panels from the left to build your tearsheet layout
            </Typography>
          </div>
          <div css={headerActionsStyle}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<DownloadIcon />}
              onClick={handleTestGenerate}
              disabled={selectedCompanyId === null || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Test Generate'}
            </Button>
            <Button
              variant='outlined'
              size='small'
              startIcon={<RestartAltIcon />}
              onClick={resetLayout}
              disabled={layout.root === null}
            >
              Reset Layout
            </Button>
          </div>
        </div>

        <div css={mainContentStyle}>
          <PanelPalette usedPanels={usedPanels} />
          <LayoutCanvas
            layout={layout}
            activeDropTarget={activeDropTarget}
            onRemovePanel={removePanel}
            onSizesChange={updateSizes}
          />
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragId && <div css={dragOverlayStyle}>{getDragOverlayLabel()}</div>}
      </DragOverlay>
    </DndContext>
  );
}
